import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ArrowLeft, Save, ArrowUp, ArrowDown, Printer, Wrench } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/tools/rack-layout")({
  head: () => ({ meta: [{ title: "Rack Layout Designer" }, { name: "robots", content: "noindex" }] }),
  component: RackPage,
});

type Side = "front" | "back";
type Item = { id: string; label: string; ru: number; color?: string; side?: Side };
type Rack = {
  id?: string; name: string; client_name: string | null; ru_height: number;
  items: Item[]; notes: string | null;
};

const empty = (): Rack => ({ name: "", client_name: "", ru_height: 42, items: [], notes: "" });
const COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#64748b"];
const sideOf = (it: Item): Side => (it.side === "back" ? "back" : "front");

/** Greedily fill free RU with 3U / 2U / 1U blank panels. */
function blankBreakdown(freeRU: number) {
  let left = Math.max(0, freeRU);
  const out: { size: number; qty: number }[] = [];
  for (const size of [3, 2, 1]) {
    const qty = Math.floor(left / size);
    if (qty > 0) { out.push({ size, qty }); left -= qty * size; }
  }
  return out;
}

function RackPage() {
  const [list, setList] = useState<Rack[]>([]);
  const [current, setCurrent] = useState<Rack | null>(null);
  const [screwsPerItem, setScrewsPerItem] = useState(4);
  const [ruPerLacingBar, setRuPerLacingBar] = useState(4);

  const load = async () => {
    const { data } = await supabase.from("rack_layouts").select("*").order("created_at", { ascending: false });
    setList((data as any) || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!current) return;
    if (!current.name) return toast.error("Name required");
    const payload = { ...current };
    if (current.id) {
      const { error } = await supabase.from("rack_layouts").update(payload).eq("id", current.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase.from("rack_layouts").insert(payload as any);
      if (error) return toast.error(error.message);
    }
    toast.success("Saved"); setCurrent(null); load();
  };

  const del = async (id: string) => {
    if (!confirm("Delete this rack layout?")) return;
    await supabase.from("rack_layouts").delete().eq("id", id);
    load();
  };

  const addItem = (side: Side) => setCurrent(c => c && ({ ...c, items: [...c.items, { id: crypto.randomUUID(), label: "Component", ru: 1, side, color: COLORS[c.items.length % COLORS.length] }] }));
  const updateItem = (id: string, patch: Partial<Item>) => setCurrent(c => c && ({ ...c, items: c.items.map(it => it.id === id ? { ...it, ...patch } : it) }));
  const removeItem = (id: string) => setCurrent(c => c && ({ ...c, items: c.items.filter(it => it.id !== id) }));
  const move = (id: string, dir: -1 | 1) => setCurrent(c => {
    if (!c) return c;
    const items = [...c.items];
    const i = items.findIndex(it => it.id === id);
    if (i < 0) return c;
    const side = sideOf(items[i]);
    // find neighbour index on the same side
    let j = i + dir;
    while (j >= 0 && j < items.length && sideOf(items[j]) !== side) j += dir;
    if (j < 0 || j >= items.length) return c;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...c, items };
  });

  const height = current?.ru_height || 0;
  const front = (current?.items || []).filter(it => sideOf(it) === "front");
  const back = (current?.items || []).filter(it => sideOf(it) === "back");
  const usedFront = front.reduce((s, it) => s + it.ru, 0);
  const usedBack = back.reduce((s, it) => s + it.ru, 0);
  const freeFront = height - usedFront;
  const freeBack = height - usedBack;

  const frontBlanks = blankBreakdown(freeFront);
  const totalBlanks = frontBlanks.reduce((s, b) => s + b.qty, 0);
  const lacingBars = Math.max(0, Math.ceil(freeBack > 0 ? height / Math.max(1, ruPerLacingBar) : 0));
  const screws = (front.length + totalBlanks) * screwsPerItem + back.length * screwsPerItem + lacingBars * 4;

  const renderRack = (items: Item[], label: string, fillBlanks: boolean) => {
    const rows: React.ReactNode[] = [];
    let ru = height;
    for (const it of items) {
      rows.push(
        <div key={it.id} style={{ height: it.ru * 24, background: it.color }} className="w-[320px] border-b border-foreground/30 flex items-center justify-between px-2 text-white text-sm font-medium">
          <span className="truncate">{it.label}</span><span className="opacity-80 shrink-0">{it.ru}RU</span>
        </div>
      );
      ru -= it.ru;
    }
    for (let i = 0; i < ru; i++) rows.push(
      <div key={"e" + i} className={"w-[320px] h-6 border-b " + (fillBlanks ? "border-foreground/20 bg-muted/60 flex items-center justify-center text-[10px] text-muted-foreground" : "border-dashed border-foreground/10")}>
        {fillBlanks ? "blank" : null}
      </div>
    );
    return (
      <div>
        <h3 className="text-sm font-semibold mb-1">{label}</h3>
        <div className="inline-block border-2 border-foreground/40 bg-background">{rows}</div>
      </div>
    );
  };

  const itemEditor = (items: Item[], side: Side) => (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="font-semibold capitalize">{side} (top→bottom)</h3>
        <Button size="sm" onClick={() => addItem(side)}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="mt-2 space-y-2">
        {items.map(it => (
          <div key={it.id} className="rounded border border-border p-2 bg-card">
            <div className="flex gap-1 items-center">
              <input type="color" value={it.color || "#6366f1"} onChange={e => updateItem(it.id, { color: e.target.value })} className="h-8 w-8 rounded" />
              <Input value={it.label} onChange={e => updateItem(it.id, { label: e.target.value })} className="flex-1" />
              <NumberInput min={1} value={it.ru} onValueChange={n => updateItem(it.id, { ru: Math.max(1, n) })} className="w-16" />
            </div>
            <div className="flex justify-end gap-1 mt-1">
              <button onClick={() => updateItem(it.id, { side: side === "front" ? "back" : "front" })} className="px-2 py-1 text-xs rounded hover:bg-accent">
                Move to {side === "front" ? "back" : "front"}
              </button>
              <button onClick={() => move(it.id, -1)} className="p-1 hover:bg-accent rounded"><ArrowUp className="h-3 w-3" /></button>
              <button onClick={() => move(it.id, 1)} className="p-1 hover:bg-accent rounded"><ArrowDown className="h-3 w-3" /></button>
              <button onClick={() => removeItem(it.id)} className="p-1 text-destructive"><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-muted-foreground">No {side} components.</p>}
      </div>
    </div>
  );

  if (!current) {
    return (
      <div>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-bold">Rack Layouts</h1>
          <Button onClick={() => setCurrent(empty())}><Plus className="h-4 w-4 mr-1" />New Rack</Button>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map(r => (
            <div key={r.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{r.name}</h3>
                  <p className="text-sm text-muted-foreground">{r.client_name || "—"}</p>
                  <p className="text-xs text-muted-foreground mt-1">{r.ru_height}RU · {r.items.length} items</p>
                </div>
                <button onClick={() => del(r.id!)} className="text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
              <Button variant="outline" size="sm" className="mt-3 w-full" onClick={() => setCurrent(r)}>Open</Button>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground text-sm">No rack layouts yet.</p>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between print:hidden">
        <Button variant="ghost" onClick={() => setCurrent(null)}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()}><Printer className="h-4 w-4 mr-1" />Print</Button>
          <Button onClick={save}><Save className="h-4 w-4 mr-1" />Save</Button>
        </div>
      </div>
      <div className="mt-4 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="grid grid-cols-2 gap-3 print:hidden">
            <div><label className="text-xs text-muted-foreground">Name</label><Input value={current.name} onChange={e => setCurrent({ ...current, name: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground">Client</label><Input value={current.client_name || ""} onChange={e => setCurrent({ ...current, client_name: e.target.value })} /></div>
            <div><label className="text-xs text-muted-foreground">Rack Height (RU)</label><NumberInput min={1} value={current.ru_height} onValueChange={n => setCurrent({ ...current, ru_height: n })} /></div>
            <div className="flex items-end text-sm">
              <span className={freeFront < 0 || freeBack < 0 ? "text-destructive" : "text-muted-foreground"}>
                Front {usedFront}/{height}RU · Back {usedBack}/{height}RU
              </span>
            </div>
          </div>
          <div className="mt-4 print:mt-0">
            <h2 className="font-semibold mb-2">{current.name || "Rack"}</h2>
            <div className="flex flex-wrap gap-8">
              {renderRack(front, "Front", true)}
              {renderRack(back, "Rear", false)}
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-border bg-card p-4">
            <h3 className="font-semibold flex items-center gap-2"><Wrench className="h-4 w-4" />Hardware Takeoff</h3>
            <div className="mt-3 grid gap-4 sm:grid-cols-3">
              <div>
                <p className="text-2xl font-bold">{totalBlanks}</p>
                <p className="text-xs text-muted-foreground">Blank panels ({Math.max(0, freeFront)}RU open on front)</p>
                <ul className="mt-1 text-xs text-muted-foreground">
                  {frontBlanks.map(b => <li key={b.size}>{b.qty} × {b.size}RU blank</li>)}
                  {totalBlanks === 0 && <li>Front fully populated</li>}
                </ul>
              </div>
              <div>
                <p className="text-2xl font-bold">{lacingBars}</p>
                <p className="text-xs text-muted-foreground">Lacing bars (1 per {ruPerLacingBar}RU of rear)</p>
                <div className="mt-1 w-24 print:hidden">
                  <NumberInput min={1} value={ruPerLacingBar} onValueChange={n => setRuPerLacingBar(Math.max(1, n))} />
                </div>
              </div>
              <div>
                <p className="text-2xl font-bold">{screws}</p>
                <p className="text-xs text-muted-foreground">Rack screws ({screwsPerItem} per panel + 4 per lacing bar)</p>
                <div className="mt-1 w-24 print:hidden">
                  <NumberInput min={1} value={screwsPerItem} onValueChange={n => setScrewsPerItem(Math.max(1, n))} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="print:hidden space-y-6">
          {itemEditor(front, "front")}
          {itemEditor(back, "back")}
          <Textarea placeholder="Notes" value={current.notes || ""} onChange={e => setCurrent({ ...current, notes: e.target.value })} />
        </div>
      </div>
    </div>
  );
}
